/**
 * @fileoverview The fixed banner shown on every page while an admin is
 * viewing as a gardener (card E16): "Viewing <name>'s garden" and Exit.
 *
 * Where: Layout, inside the signed-in frame. It sits fixed at the foot (above
 * the tab bar on a phone) so it never moves the header. Shows nothing when no
 * view is on. Exit empties the caches and reloads as the admin (shared/api/viewAs).
 * Words (en/es): viewAs.banner, viewAs.exit. Colours: theme (highlight).
 *
 * @module ViewAsBanner
 */

import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { exitViewAs, viewingAs } from '@/shared/api/viewAs';

/**
 * The "Viewing <name>'s garden" bar with its Exit button.
 *
 * @returns The banner, or nothing when no view is on.
 */
export function ViewAsBanner(): JSX.Element | null {
  const { reactTranslate: t } = useTranslation();
  const who = viewingAs();
  if (!who) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-20 z-[60] mx-auto flex w-fit max-w-[calc(100%-2rem)] items-center gap-3 rounded-full bg-highlight px-4 py-2 text-sm text-highlight-foreground shadow-lg md:bottom-4"
    >
      <span className="truncate">{t('viewAs.banner', { name: who.name })}</span>
      <span aria-hidden="true">·</span>
      <Button size="sm" variant="secondary" onClick={exitViewAs}>{t('viewAs.exit')}</Button>
    </div>
  );
}
